import { type FC } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import {
  DirectionFacingLabel,
  FrameMaterialLabel,
  GlazingPanesLabel,
  GlazingPanesOptions,
  OperabilityLabel,
} from '#apps/client-web/features/audit-form-windows/constants';
import { FileInput } from '#apps/client-web/features/file-storage/components/file-input';

import {
  type DirectionFacing,
  type FrameMaterial,
  type Operability,
} from '#packages/data-collection/models/audit-windows-form';

import { type EditAuditFormWindowsEntry } from './types';

export type AuditFormWindowsEntryPageRendererProps = {
  form: EditAuditFormWindowsEntry;
  isPending: boolean;
  onSubmit: () => void;
};

export const AuditFormWindowsEntryPageRenderer: FC<
  AuditFormWindowsEntryPageRendererProps
> = ({ form, isPending, onSubmit }) => {
  const { control } = form;
  const disableForm = isPending;

  const isUsingMetrics = useWatch({ control, name: 'isUsingMetrics' });

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      display="flex"
      flexDirection="column"
      flex={1}>
      <Stack spacing={3} sx={{ mt: 2 }} flex={1}>
        {/* Section */}
        <Controller
          control={control}
          name="section"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Section"
                type="text"
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Direction Facing */}
        <Controller
          control={control}
          name="directionFacing"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
              <Autocomplete
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                options={Object.keys(DirectionFacingLabel)}
                getOptionLabel={(option) =>
                  DirectionFacingLabel[option as DirectionFacing] ?? option
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Direction Facing"
                    variant="outlined"
                    {...getFormErrorAttributes(fieldState.error)}
                  />
                )}
                value={field.value ?? null}
                onChange={(_, value) => field.onChange(value)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Quantity */}
        <Controller
          control={control}
          name="quantity"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Quantity"
                type="number"
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Metric toggle */}
        <Controller
          control={control}
          name="isUsingMetrics"
          disabled={disableForm}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value ?? false}
                  onChange={(e) => field.onChange(e.target.checked)}
                  disabled={disableForm}
                />
              }
              label="Use metric measurements"
            />
          )}
        />

        {/* Imperial fields */}
        {!isUsingMetrics && (
          <>
            <Typography variant="body2" color="textSecondary">
              Width
            </Typography>
            <Stack direction="row" spacing={2}>
              <Controller
                control={control}
                name="widthInFeet"
                disabled={disableForm}
                render={({ field, fieldState }) => (
                  <FormControl
                    fullWidth
                    {...getFormErrorAttributes(fieldState.error)}>
                    <TextField
                      {...field}
                      {...getFormErrorAttributes(fieldState.error)}
                      label="Width"
                      type="number"
                      variant="outlined"
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">ft</InputAdornment>
                          ),
                        },
                      }}
                    />
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="widthInInches"
                disabled={disableForm}
                render={({ field, fieldState }) => (
                  <FormControl
                    fullWidth
                    {...getFormErrorAttributes(fieldState.error)}>
                    <TextField
                      {...field}
                      {...getFormErrorAttributes(fieldState.error)}
                      label="Width"
                      type="number"
                      variant="outlined"
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">in</InputAdornment>
                          ),
                        },
                      }}
                    />
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Stack>
            <Typography variant="body2" color="textSecondary">
              Height
            </Typography>
            <Stack direction="row" spacing={2}>
              <Controller
                control={control}
                name="heightInFeet"
                disabled={disableForm}
                render={({ field, fieldState }) => (
                  <FormControl
                    fullWidth
                    {...getFormErrorAttributes(fieldState.error)}>
                    <TextField
                      {...field}
                      {...getFormErrorAttributes(fieldState.error)}
                      label="Height"
                      type="number"
                      variant="outlined"
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">ft</InputAdornment>
                          ),
                        },
                      }}
                    />
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="heightInInches"
                disabled={disableForm}
                render={({ field, fieldState }) => (
                  <FormControl
                    fullWidth
                    {...getFormErrorAttributes(fieldState.error)}>
                    <TextField
                      {...field}
                      {...getFormErrorAttributes(fieldState.error)}
                      label="Height"
                      type="number"
                      variant="outlined"
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">in</InputAdornment>
                          ),
                        },
                      }}
                    />
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Stack>
          </>
        )}

        {/* Metric fields */}
        {isUsingMetrics && (
          <Stack direction="row" spacing={2}>
            <Controller
              control={control}
              name="widthInMeters"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Width"
                    type="number"
                    variant="outlined"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">m</InputAdornment>
                        ),
                      },
                    }}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="heightInMeters"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Height"
                    type="number"
                    variant="outlined"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">m</InputAdornment>
                        ),
                      },
                    }}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Stack>
        )}

        {/* Area (calculated, disabled) */}
        <Controller
          control={control}
          name="area"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label={`Area (${isUsingMetrics ? 'm²' : 'ft²'})`}
              type="number"
              variant="outlined"
              disabled
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {isUsingMetrics ? 'm²' : 'ft²'}
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />

        {/* Operability */}
        <Controller
          control={control}
          name="operability"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
              <Autocomplete
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                options={Object.keys(OperabilityLabel)}
                getOptionLabel={(option) =>
                  OperabilityLabel[option as Operability] ?? option
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Operability"
                    variant="outlined"
                    {...getFormErrorAttributes(fieldState.error)}
                  />
                )}
                value={field.value ?? null}
                onChange={(_, value) => field.onChange(value)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Frame Material */}
        <Controller
          control={control}
          name="frameMaterial"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
              <Autocomplete
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                options={Object.keys(FrameMaterialLabel)}
                getOptionLabel={(option) =>
                  FrameMaterialLabel[option as FrameMaterial] ?? option
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Frame Material"
                    variant="outlined"
                    {...getFormErrorAttributes(fieldState.error)}
                  />
                )}
                value={field.value ?? null}
                onChange={(_, value) => field.onChange(value)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Glazing Panes */}
        <Controller
          control={control}
          name="glazingPanes"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
              <Autocomplete
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                options={GlazingPanesOptions}
                getOptionLabel={(option) => GlazingPanesLabel[option] ?? String(option)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Glazing (Panes)"
                    variant="outlined"
                    {...getFormErrorAttributes(fieldState.error)}
                  />
                )}
                value={field.value ?? null}
                onChange={(_, value) => field.onChange(value)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Notes */}
        <Controller
          control={control}
          name="notes"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Notes"
                type="text"
                multiline
                rows={3}
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Attachments */}
        <Controller
          control={control}
          name="attachments"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
              <FileInput
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Attachments"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Stack>
    </Box>
  );
};
